import { useState } from 'react';
import { Button, Drawer } from '@scholastic/mockingjay';
import CheckIcon from '@data/assets/check.svg';
import ChevronRightIcon from '@data/assets/chevronRight.svg';

export interface SkillDrawerOption {
  id: number;
  name: string;
}

interface SkillDrawerProps {
  options: SkillDrawerOption[];
  selectedId: number;
  onApply: (id: number) => void;
}

/**
 * The Skill select for Tablet and Mobile: a button beside the table caption
 * opening a drawer of every skill. A pick only marks a skill until Apply.
 */
export function SkillDrawer({
  options,
  selectedId,
  onApply,
}: SkillDrawerProps) {
  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState(selectedId);
  const selectedName = options.find((o) => o.id === selectedId)?.name ?? '';

  const handleOpenChange = (next: boolean) => {
    if (next) setPendingId(selectedId);
    setOpen(next);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      size="xs"
      direction="right"
      title="Skill"
      className="max-w-[90vw]"
      trigger={
        <Button
          variant="secondary"
          aria-label={`Skill: ${selectedName}`}
          aria-haspopup="dialog"
          iconRight={<ChevronRightIcon aria-hidden className="size-4" />}
          className="!size-11 !min-w-0 shrink-0 !space-x-0 !p-0 tablet:!w-auto tablet:!space-x-2 tablet:!px-4"
        >
          <span className="hidden tablet:inline">Skill</span>
        </Button>
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          <Button variant="tertiary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onApply(pendingId);
              setOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      }
    >
      <fieldset className="-m-4">
        <legend className="sr-only">Skill</legend>
        {options.map((option) => (
          <label
            key={option.id}
            htmlFor={`skill-option-${option.id}`}
            className="flex min-h-11 cursor-pointer items-start gap-2 px-4 py-3 text-sm text-neutral-1300 hover:bg-neutral-100 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-brand-captainBlue"
          >
            <input
              id={`skill-option-${option.id}`}
              type="radio"
              name="skill"
              value={option.id}
              checked={option.id === pendingId}
              onChange={() => setPendingId(option.id)}
              className="peer sr-only"
            />
            <CheckIcon
              aria-hidden
              className="mt-1 h-2 w-2.5 shrink-0 opacity-0 peer-checked:opacity-100"
            />
            <span className="line-clamp-2">{option.name}</span>
          </label>
        ))}
      </fieldset>
    </Drawer>
  );
}
